import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { confirmDetailsSchema } from '../schema'
import { z } from 'zod'
import { useAuthStore } from '@/store/auth-store'
import { useModal } from '@/components/Modal'
import { useGetConfirmDetails } from './useGetConfirmDetails'
import { IUserShift } from '@/types/user'
import { useCreateAutomaticStartShiftLog } from './useCreateAutomaticStartShiftLog'
import { useWorkstationSelect } from '@/modules/Workstation/hooks/useWorkstationSelect'

type FormValues = z.infer<typeof confirmDetailsSchema>

interface IConfirmDetailsRequest {
    badgeNumber?: string | null
    busHubCode: string
    dutyCode?: string | null
}

export const useSendConfirmDetails = () => {
    const { setXUserShiftId, setEndOfShiftTime, setPreviousDutyCode, setStartOfShiftTime } = useAuthStore()
    const queryClient = useQueryClient()
    const { data: confirmDetailsData } = useGetConfirmDetails()
    const { closeModal } = useModal()
    const { handleCreateAutomaticStartShiftLog } = useCreateAutomaticStartShiftLog()
    const { handleWorkstationChange } = useWorkstationSelect()

    const sendConfirmDetailsMutation = useAppMutation<IUserShift, Error, IConfirmDetailsRequest>({
        mutationFn: (data) =>
            createInstance({
                url: '/user-shifts',
                method: 'POST',
                data,
            }),
        onSuccess: async (data) => {
            await setXUserShiftId(data.id)
            await setStartOfShiftTime(data.dutyFrom)
            await setEndOfShiftTime(data.dutyTo)
            await setPreviousDutyCode(data.previousDutyCode)
            await queryClient.invalidateQueries({ queryKey: ['user-info'] })
            handleWorkstationChange(data.workstation)

            handleCreateAutomaticStartShiftLog(data, data.workstation)

            closeModal()
        },
    })

    const handleSendConfirmDetails = (data: FormValues) => {
        if (!confirmDetailsData) throw new Error('Confirm details data not found')
        const busHubCode = confirmDetailsData.hubs.find((hub) => hub.hubTitle === data.busHubCode)?.iBusCode
        if (!busHubCode) throw new Error('Hub not found')

        sendConfirmDetailsMutation.mutate({
            badgeNumber: data.noBadge ? null : data.badgeNumber,
            busHubCode,
            dutyCode: data.noDuty ? null : data.dutyCode,
        })
    }

    return { handleSendConfirmDetails, isPending: sendConfirmDetailsMutation.isPending }
}
